import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { BackHandler } from 'react-native';
import { useRecoilState, useResetRecoilState } from 'recoil';

import { vodPlaylistState } from '@store';

import { getNextVideoData, getPreviousVideoData, onVideoEnd, onVideoProgress } from './helpers';
import { EpisodeData, HelpersData, Params, VideoPlayerReference, VODPlaylistReference } from './types';

export const useFullScreen = (
  {
    videoID,
    playlist,
    playlistEpisodeIndex,
    playlistSeasonIndex,
    seasonID,
    needsBookmarking,
    startPosition,
    goNext,
    goPrevious,
  }: Params,
  videoPlayerReference: MutableRefObject<VideoPlayerReference | null>,
  vodPlaylistReference: MutableRefObject<VODPlaylistReference | null>,
  vodFirstFocusReference?: unknown,
) => {
  const resetVodPlaylist = useResetRecoilState(vodPlaylistState);

  const [vodID, setVodID] = useState(videoID);
  const [episodeIndex, setEpisodeIndex] = useState(playlistEpisodeIndex ?? -1);
  const [seasonIndex, setSeasonIndex] = useState(playlistSeasonIndex ?? -1);
  const [showNextEpisodeOverlay, setShowNextEpisodeOverlay] = useState(false);
  const [exitedShowNextEpisodeOverlay, setExitedShowNextEpisodeOverlay] = useState(false);
  const [currentVODPlaylistState, setVODPLaylist] = useRecoilState(vodPlaylistState);

  const [nextEpisodeData, setNextEpisodeData] = useState<EpisodeData>({
    vodID: videoID,
    episodeIndex: playlistEpisodeIndex ?? -1,
    seasonIndex: playlistSeasonIndex ?? -1,
  });

  const helpersData = useMemo<HelpersData>(
    () => ({
      playlist,
      vodID,
      setVodID,
      seasonIndex,
      setSeasonIndex,
      episodeIndex,
      setEpisodeIndex,
      nextEpisodeData,
      setNextEpisodeData,
      showNextEpisodeOverlay,
      setShowNextEpisodeOverlay,
      exitedShowNextEpisodeOverlay,
      setExitedShowNextEpisodeOverlay,
      currentVODPlaylistState,
      setVODPLaylist,
      videoPlayerReference,
      vodPlaylistReference,
      vodFirstFocusReference,
    }),
    [
      playlist,
      vodID,
      seasonIndex,
      episodeIndex,
      nextEpisodeData,
      showNextEpisodeOverlay,
      exitedShowNextEpisodeOverlay,
      currentVODPlaylistState,
      setVODPLaylist,
      videoPlayerReference,
      vodPlaylistReference,
      vodFirstFocusReference,
    ],
  );

  useEffect(
    () => () => {
      if (vodPlaylistReference?.current) {
        vodPlaylistReference.current?.hidePlayList();
        vodPlaylistReference.current?.hideControls();
      }
      resetVodPlaylist();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    setVodID(videoID);
  }, [videoID]);

  useEffect(() => {
    if (goNext) {
      const newData = getNextVideoData(helpersData, false, true);

      if (newData.episodeIndex >= 0) {
        setEpisodeIndex(newData.episodeIndex);
      }
      if (newData.seasonIndex >= 0) {
        setSeasonIndex(newData.seasonIndex);
      }
      if (newData.vodID) {
        setVodID(newData.vodID);
      }
    }
  }, [goNext, helpersData]);

  useEffect(() => {
    if (goPrevious) {
      const newData = getPreviousVideoData(helpersData);

      if (newData.episodeIndex >= 0) {
        setEpisodeIndex(newData.episodeIndex);
      }
      if (newData.seasonIndex >= 0) {
        setSeasonIndex(newData.seasonIndex);
      }
      if (newData.vodID) {
        setVodID(newData.vodID);
      }
    }
  }, [goPrevious, helpersData]);

  const onBackPress = useCallback(() => {
    if (exitedShowNextEpisodeOverlay) {
      setExitedShowNextEpisodeOverlay(true);
      setShowNextEpisodeOverlay(false);
      return true;
    }

    return false;
  }, [exitedShowNextEpisodeOverlay]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    useCallback(() => {
      BackHandler.addEventListener('hardwareBackPress', onBackPress);
      return () => {
        BackHandler.removeEventListener('hardwareBackPress', onBackPress);
      };
    }, [onBackPress]),
  );

  const onVideoEndResult = useCallback(() => onVideoEnd(helpersData), [helpersData]);

  const onVideoProgressResult = useCallback(
    (progress, duration) => onVideoProgress(helpersData, progress, duration),
    [helpersData],
  );

  return {
    videoID,
    seasonID,
    needsBookmarking,
    startPosition,
    vodID,
    nextEpisodeData,
    showNextEpisodeOverlay,
    onVideoEndResult,
    onVideoProgressResult,
  };
};
